import type { ZodType } from 'zod';
import {AdsSchema,AnalysisRunSchema,AnalyzeSiteSchema,CatalogSchema,IndependentSearchSchema,CompetitorDiscoverySchema,HistorySchema,MetricsSchema,PositioningSchema,SnapshotSaveSchema,SocialSchema,StrategySchema,WatchlistMutationSchema,WatchlistSchema} from './schemas';

async function parseJson<T>(response:Response,schema:ZodType<T>,endpoint:string):Promise<T>{
  const raw=await response.json().catch(()=>null);
  if(!response.ok){
    const detail=raw&&typeof raw==='object'&&'error'in raw?String((raw as {error?:unknown}).error):'HTTP_'+response.status;
    throw new Error(endpoint+': '+detail);
  }
  const parsed=schema.safeParse(raw);
  if(!parsed.success){console.error('Schema validation failed',endpoint,parsed.error,raw);throw new Error(endpoint+': INVALID_SCHEMA');}
  return parsed.data;
}
function ciSmokeHeaders(endpoint:string):Record<string,string>{
  if(!/(?:analysis-run|snapshot-save)$/.test(endpoint))return{};
  try{
    if(typeof window!=='undefined'&&window.sessionStorage?.getItem('insight_ci_smoke')==='1'){
      return{'x-insight-ci-smoke':'1'};
    }
  }catch{}
  return{};
}
async function post<T>(endpoint:string,body:unknown,schema:ZodType<T>,signal?:AbortSignal):Promise<T>{
  // Every provider has an upper bound. One hanging source should produce a
  // partial module instead of keeping the entire report "running" forever.
  const timeout=AbortSignal.timeout(75_000);
  const bounded=signal?AbortSignal.any([signal,timeout]):timeout;
  try{
    const response=await fetch(endpoint,{method:'POST',headers:{'content-type':'application/json',...ciSmokeHeaders(endpoint)},body:JSON.stringify(body),signal:bounded,cache:'no-store'});
    return parseJson(response,schema,endpoint);
  }catch(err){
    if(timeout.aborted&&!signal?.aborted)throw new Error(endpoint+': PROVIDER_TIMEOUT');
    throw err;
  }
}
const EDGE_BASE_URL=((import.meta.env.VITE_EDGE_BASE_URL as string|undefined)?.trim()||'https://merexzrfmriwpgzbfozn.supabase.co/functions/v1').replace(/\/$/,'');
const edge=(slug:string)=>EDGE_BASE_URL+'/'+slug;

async function get<T>(endpoint:string,schema:ZodType<T>,signal?:AbortSignal):Promise<T>{
  const timeout=AbortSignal.timeout(20_000);
  const bounded=signal?AbortSignal.any([signal,timeout]):timeout;
  try{
    const response=await fetch(endpoint,{method:'GET',signal:bounded,cache:'no-store'});
    return parseJson(response,schema,endpoint);
  }catch(err){
    if(timeout.aborted&&!signal?.aborted)throw new Error(endpoint+': PROVIDER_TIMEOUT');
    throw err;
  }
}
export const api={
  analyzeSite:(url:string,signal:AbortSignal)=>post(edge('analyze-site'),{url},AnalyzeSiteSchema,signal),
  catalog:(url:string,signal:AbortSignal)=>post(edge('catalog-intelligence'),{url},CatalogSchema,signal),
  search:(url:string,signal:AbortSignal)=>post(edge('search-intelligence'),{url,market:'us',lang:'en'},IndependentSearchSchema,signal),
  metrics:(url:string,signal:AbortSignal,analysisRunId?:string)=>post(edge('metrics-estimate'),{url,market:'us',lang:'en',analysisRunId},MetricsSchema,signal),
  positioning:(url:string,signal:AbortSignal)=>post(edge('business-positioning'),{url},PositioningSchema,signal),
  social:(url:string,signal:AbortSignal)=>post(edge('social-public'),{url},SocialSchema,signal),
  ads:(url:string,signal:AbortSignal)=>post(edge('ads-public-signals'),{url,market:'us',lang:'en'},AdsSchema,signal),
  strategy:(url:string,signal:AbortSignal)=>post(edge('social-strategy'),{url},StrategySchema,signal),
  competitorDiscovery:(url:string,signal?:AbortSignal)=>post(edge('competitor-discovery'),{url,market:'us',lang:'en'},CompetitorDiscoverySchema,signal),
  watchlist:(signal?:AbortSignal)=>get(edge('watchlist'),WatchlistSchema,signal),
  watchlistAdd:(input:{domain:string;url?:string;name?:string|null;platform?:string|null;cadence?:'daily'|'weekly'|'monthly'},signal?:AbortSignal)=>post(edge('watchlist'),{action:'add',...input},WatchlistMutationSchema,signal),
  watchlistUpdate:(domain:string,patch:{cadence?:'daily'|'weekly'|'monthly';enabled?:boolean},signal?:AbortSignal)=>post(edge('watchlist'),{action:'update',domain,...patch},WatchlistMutationSchema,signal),
  watchlistRemove:(domain:string,signal?:AbortSignal)=>post(edge('watchlist'),{action:'remove',domain},WatchlistMutationSchema,signal),
  saveSnapshot:(payload:unknown,signal:AbortSignal)=>post(edge('snapshot-save'),payload,SnapshotSaveSchema,signal),
  history:(domain:string,optionsOrSignal?:{range?:'30d'|'90d'|'1y'|'all';grain?:'daily'|'monthly'|'yearly'}|AbortSignal,signal?:AbortSignal)=>{const isSignal=Boolean(optionsOrSignal&&'aborted'in optionsOrSignal);const options=isSignal?undefined:optionsOrSignal as {range?:'30d'|'90d'|'1y'|'all';grain?:'daily'|'monthly'|'yearly'}|undefined;const actualSignal=isSignal?optionsOrSignal as AbortSignal:signal;const p=new URLSearchParams({domain,limit:'1000',range:options?.range||'all',grain:options?.grain||'daily'});return get(edge('history')+'?'+p.toString(),HistorySchema,actualSignal)},
  analysisRunStart:(runId:string,domain:string,signal:AbortSignal)=>post(edge('analysis-run'),{action:'start',runId,domain,modelVersion:'v2.1_xstate'},AnalysisRunSchema,signal),
  analysisRunUpdate:(runId:string,status:string,moduleStatus:unknown,signal:AbortSignal,errorSummary?:string)=>post(edge('analysis-run'),{action:'update',runId,status,moduleStatus,errorSummary},AnalysisRunSchema,signal)
};
export function normalizeDomain(value:string):string{
  const trimmed=value.trim();if(!trimmed)return'';
  try{const url=new URL(/^https?:\/\//i.test(trimmed)?trimmed:'https://'+trimmed);return url.hostname.replace(/^www\./i,'').toLowerCase();}catch{return'';}
}
export function normalizeUrl(value:string):string{const domain=normalizeDomain(value);if(!domain)throw new Error('INVALID_DOMAIN');return'https://'+domain+'/';}
